import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useResource, formatDate, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm } from "../components/EntityForm";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { Participant, Referral } from "../lib/types";
import { type QuarantineRow, streamOf } from "../lib/intakeStreams";
import { containsLink } from "../lib/intakeDisplay";
import {
    STATUS, URGENCY, prefillFromPayload, nestedFromPayload, buildReferralFields,
} from "../lib/referralIntake";

const urgencyChip = (u: Referral["urgency"]) =>
    u === "urgent" ? "block" : u === "high" ? "warn" : "mute";

const statusChip = (s: string) =>
    s === "new" ? "warn" : s === "closed" ? "mute" : s === "active" || s === "allocated" ? "pass" : "info";

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; referral: Referral }
    | { kind: "accept"; referral: Referral }
    | { kind: "reject"; referral: Referral }
    | { kind: "promote"; item: QuarantineRow }
    | { kind: "discard"; item: QuarantineRow }
    | null;

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

export const Referrals = () => {
    const { user } = useAuth();
    const canDecide = user?.role === "admin" || user?.role === "manager";

    const referrals = useResource<Referral[]>("/referrals");
    const quarantine = useResource<QuarantineRow[]>(canDecide ? "/intake/quarantine" : null);
    const serviceList = useResource<Array<{ name: string; is_high_intensity: boolean }>>("/referral-services");
    const cases = useResource<Array<{ id: string; ref_code: string | null; referral_id: string | null; status: string }>>("/cases");
    const participants = useResource<Participant[]>("/participants");
    const companies = useResource<Array<{ id: string; name: string }>>("/companies");
    const contacts = useResource<Array<{ id: string; first_name: string; last_name: string; company_id: string | null }>>("/contacts");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);
    const [confirmBusyId, setConfirmBusyId] = useState<string | null>(null);
    const [confirmError, setConfirmError] = useState<string | null>(null);

    const refresh = () => { referrals.reload(); quarantine.reload(); setDialog(null); };

    // Only referral-intent submissions belong on this page. Every other website
    // form (newsletter, callback, job applications, feedback...) has its own
    // home — a newsletter signup is not a referral. The classifier is shared
    // with the intake trigger so the queue matches what drew a TC-REF number.
    const referralQueue = (quarantine.data ?? []).filter(
        (q) => streamOf(q.form_name) === "referral"
    );

    // Promote = the referral was just created through the normal form; stamp
    // the quarantine row with what it became so the queue empties honestly.
    const promote = async (item: QuarantineRow, saved?: unknown) => {
        const referralId = (saved as { id?: string } | undefined)?.id;
        if (!referralId) { refresh(); return; }
        try {
            await api.post(`/intake/quarantine/${item.id}/promote`, { referralId });
        } catch (err) {
            setConfirmError(err instanceof Error ? err.message : "Created the referral but could not clear the queue item");
        }
        refresh();
    };

    const caseFor = useMemo(() => {
        const map = new Map((cases.data ?? []).filter((c) => c.referral_id).map((c) => [c.referral_id!, c]));
        return (referralId: string) => map.get(referralId);
    }, [cases.data]);

    // One-click push onto the sales pipeline: a lead prefilled from the
    // referral, then the referral remembers it (linked_lead_id). Deliberate,
    // not automatic — not every referral is a sales event.
    const addToPipeline = async (referral: Referral) => {
        setConfirmBusyId(referral.id);
        setConfirmError(null);
        try {
            const lead = await api.post<{ id: string }>("/leads", {
                title: `Referral ${referral.ref_code ?? ""} — ${referral.referring_organisation ?? referral.referring_contact_name}`.trim(),
                stage: "new_inquiry",
                source: "Referral",
                service_type: referral.services[0] ?? null,
                company_id: referral.company_id ?? null,
                contact_id: referral.contact_id ?? null,
                notes: `Created from referral ${referral.ref_code ?? referral.id}.`,
            });
            await api.put(`/referrals/${referral.id}`, {
                referring_contact_name: referral.referring_contact_name,
                referring_contact_phone: referral.referring_contact_phone,
                linked_lead_id: lead.id,
            });
            referrals.reload();
        } catch (err) {
            setConfirmError(err instanceof Error ? err.message : "Could not add to the pipeline");
        } finally {
            setConfirmBusyId(null);
        }
    };

    const sendConfirmation = async (referral: Referral) => {
        setConfirmBusyId(referral.id);
        setConfirmError(null);
        try {
            await api.post(`/referrals/${referral.id}/send-confirmation`, {});
            referrals.reload();
        } catch (err) {
            setConfirmError(err instanceof Error ? err.message : "Could not send the confirmation");
        } finally {
            setConfirmBusyId(null);
        }
    };

    const participantName = useMemo(() => {
        const map = new Map((participants.data ?? []).map((p) => [p.id, p.full_name]));
        return (id: string | null) => (id ? map.get(id) ?? "Unknown" : null);
    }, [participants.data]);

    // HIDPA skill areas, from the managed service list — a referral asking for
    // one is flagged so it's obvious it needs signed-off workers.
    const highIntensity = useMemo(() => new Set(
        (serviceList.data ?? []).filter((s) => s.is_high_intensity).map((s) => s.name.toLowerCase())
    ), [serviceList.data]);

    // The referral field set — shared with the Enquiry queue's promote flow, so
    // a submission promoted from either place produces an identical referral.
    const fieldDeps = {
        companies: companies.data ?? [],
        contacts: contacts.data ?? [],
        participants: participants.data ?? [],
    };
    const fields = buildReferralFields(fieldDeps);

    const filtered = (referrals.data ?? []).filter((r) =>
        matches(search, r.ref_code, r.referring_contact_name, r.referring_organisation, participantName(r.participant_id)) &&
        (!filters.status || r.status === filters.status) &&
        (!filters.urgency || r.urgency === filters.urgency)
    );

    if (referrals.error) return <div className="error-banner">{referrals.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Add Referral" path="/referrals" fields={fields}
                    subtitle="Use this for referrals taken by phone or email. Website referrals arrive automatically."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title="Edit referral" path="/referrals" fields={fields} initial={dialog.referral}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "promote" && (
                <EntityForm title={`Promote website submission to referral${dialog.item.ref_code ? ` — ${dialog.item.ref_code}` : ""}`}
                    path="/referrals"
                    fields={buildReferralFields({ ...fieldDeps, nested: nestedFromPayload(dialog.item.payload) })}
                    initial={prefillFromPayload(dialog.item.payload)}
                    fixed={dialog.item.ref_code ? { ref_code: dialog.item.ref_code } : undefined}
                    subtitle="Prefilled from the website form — check it, link or create the participant, then save. The referral keeps the reference number the website email already quoted."
                    onClose={() => setDialog(null)}
                    onSaved={(saved) => void promote(dialog.item, saved)} />
            )}
            {dialog?.kind === "discard" && (
                <ConfirmDialog
                    title="Reject this website submission?"
                    tone="danger"
                    message="It is removed from the intake queue and no referral is created."
                    confirmLabel="Reject submission" requireReason reasonLabel="Why is it rejected? (spam, duplicate, not a referral…)"
                    request={{ method: "post", path: `/intake/quarantine/${dialog.item.id}/reject` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "accept" && (
                <ConfirmDialog
                    title="Accept this referral?"
                    message={`It moves to Allocated, becomes active work for ${participantName(dialog.referral.participant_id) ?? "the participant"}, and a case file (TC number) opens automatically for the episode.`}
                    warning={!dialog.referral.participant_id
                        ? "This referral is not linked to a participant record yet. Edit it and choose a participant first — acceptance will be refused otherwise."
                        : undefined}
                    confirmLabel="Accept referral"
                    request={{ method: "post", path: `/referrals/${dialog.referral.id}/accept` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "reject" && (
                <ConfirmDialog
                    title="Reject this referral?"
                    tone="danger"
                    message="It is closed and will not proceed to service."
                    warning="A rejected referral is a decision the organisation may have to account for, so the reason is recorded permanently against the record and in the audit log."
                    confirmLabel="Reject referral" requireReason reasonLabel="Reason for rejecting"
                    request={{ method: "post", path: `/referrals/${dialog.referral.id}/reject` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search referrals…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "status", allLabel: "All Status", options: STATUS },
                    { name: "urgency", allLabel: "All Urgency", options: URGENCY },
                ]}
                resultNote={
                    filtered.length !== (referrals.data?.length ?? 0)
                        ? `Showing ${filtered.length} of ${referrals.data?.length ?? 0} referrals`
                        : undefined
                }
                action={canDecide && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Add Referral</button>
                )}
            />

            {confirmError && <div className="error-banner">{confirmError}</div>}

            {canDecide && referralQueue.length > 0 && (
                <div className="card" style={{ marginBottom: "0.85rem" }}>
                    <div className="card-head">
                        <div>
                            <h2>Website intake queue</h2>
                            <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                                Referral submissions from the website, held for review. Nothing becomes a
                                referral until you promote it — spam stops here, not in your referral list.
                                Other website forms land on the Website inbox.
                            </p>
                        </div>
                        <span className="chip warn">{referralQueue.length} pending</span>
                    </div>
                    <div className="card-body">
                        <div style={{ display: "grid", gap: "0.5rem" }}>
                            {referralQueue.map((item) => {
                                const mapped = prefillFromPayload(item.payload ?? {});
                                const who = str(mapped.referring_contact_name) || "Unknown sender";
                                const contact = [str(mapped.referring_contact_email), str(mapped.referring_contact_phone)]
                                    .filter(Boolean).join(" · ");
                                const message = str(mapped.additional_notes);
                                return (
                                    <div key={item.id} className="file-row" style={{ alignItems: "flex-start" }}>
                                        <span className="mono" style={{ whiteSpace: "nowrap" }}>{formatDate(item.received_at)}</span>
                                        <span>
                                            <div style={{ fontWeight: 600 }}>
                                                {item.ref_code && <span className="chip mute mono" style={{ marginRight: 6, fontWeight: 700 }}>{item.ref_code}</span>}
                                                {who}
                                                {item.form_name && <span className="chip info" style={{ marginLeft: 6 }}>{item.form_name}</span>}
                                                {containsLink(item.payload) && (
                                                    <span className="chip warn" style={{ marginLeft: 6 }}
                                                        title="This submission contains a web address. It is shown as plain text and is not clickable — do not open it unless you trust the sender.">
                                                        Contains a link
                                                    </span>
                                                )}
                                            </div>
                                            {contact && <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{contact}</div>}
                                            {message && (
                                                <div style={{ fontSize: "0.78rem", color: "var(--ink-soft)", marginTop: 3,
                                                    overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                    {message}
                                                </div>
                                            )}
                                        </span>
                                        <span style={{ display: "flex", gap: "0.35rem", whiteSpace: "nowrap" }}>
                                            <button className="icon-btn" onClick={() => setDialog({ kind: "promote", item })}>
                                                Promote to referral
                                            </button>
                                            <button className="icon-btn danger" onClick={() => setDialog({ kind: "discard", item })}>
                                                Reject
                                            </button>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Reference</th><th>Received</th><th>Referrer</th><th>Participant</th>
                                    <th>Services</th><th>Urgency</th><th>Status</th>
                                    {canDecide && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((referral) => {
                                    const decidable = referral.status === "new" || referral.status === "in_progress";
                                    return (
                                        <tr key={referral.id}>
                                            <td>
                                                <div className="mono" style={{ fontWeight: 600 }}>{referral.ref_code ?? "—"}</div>
                                                {referral.confirmation_sent_at && (
                                                    <div style={{ fontSize: "0.68rem", color: "var(--muted)" }}>
                                                        confirmed {formatDate(referral.confirmation_sent_at)}
                                                    </div>
                                                )}
                                                {caseFor(referral.id) && (
                                                    <Link to="/cases" className="chip pass mono"
                                                        title={`Case file — ${titleCase(caseFor(referral.id)!.status)}`}
                                                        style={{ fontWeight: 700, textDecoration: "none", marginTop: 3, display: "inline-block" }}>
                                                        {caseFor(referral.id)!.ref_code}
                                                    </Link>
                                                )}
                                                {referral.linked_lead_id && (
                                                    <div style={{ marginTop: 3 }}>
                                                        <Link to="/crm/pipeline" className="chip info" style={{ textDecoration: "none" }}>
                                                            In pipeline
                                                        </Link>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="mono">{formatDate(referral.received_at)}</td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                    <InitialsAvatar name={referral.referring_contact_name} size={28} />
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{referral.referring_contact_name}</div>
                                                        <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                                            {referral.referring_organisation ?? "—"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {participantName(referral.participant_id) ? (
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                        <InitialsAvatar name={participantName(referral.participant_id)} size={24} />
                                                        {participantName(referral.participant_id)}
                                                    </div>
                                                ) : (
                                                    <span className="chip warn">not linked</span>
                                                )}
                                            </td>
                                            <td>
                                                {referral.services.some((s) => highIntensity.has(s.toLowerCase())) && (
                                                    <span className="chip block" style={{ marginRight: 3, fontWeight: 700 }}
                                                        title="Includes HIDPA high-intensity supports — requires signed-off workers">
                                                        High intensity
                                                    </span>
                                                )}
                                                {referral.services.map((s) => (
                                                    <span key={s} className="chip info" style={{ marginRight: 3 }}>{s}</span>
                                                ))}
                                            </td>
                                            <td>
                                                <span className={`chip ${urgencyChip(referral.urgency)}`}>
                                                    {titleCase(referral.urgency)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`chip ${statusChip(referral.status)}`}>
                                                    {titleCase(referral.status)}
                                                </span>
                                            </td>
                                            {canDecide && (
                                                <td>
                                                    <div className="row-actions">
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", referral })}>Edit</button>
                                                        {referral.referring_contact_email && (
                                                            <button className="icon-btn" disabled={confirmBusyId === referral.id}
                                                                title={referral.confirmation_sent_at
                                                                    ? "Resend the reference email to the referrer"
                                                                    : "Email the referrer their reference number"}
                                                                onClick={() => void sendConfirmation(referral)}>
                                                                {confirmBusyId === referral.id ? "Sending…"
                                                                    : referral.confirmation_sent_at ? "Resend confirmation" : "Send confirmation"}
                                                            </button>
                                                        )}
                                                        {!referral.linked_lead_id && (
                                                            <button className="icon-btn" disabled={confirmBusyId === referral.id}
                                                                title="Create a pipeline lead from this referral"
                                                                onClick={() => void addToPipeline(referral)}>
                                                                Add to pipeline
                                                            </button>
                                                        )}
                                                        {decidable && (
                                                            <>
                                                                <button className="icon-btn" onClick={() => setDialog({ kind: "accept", referral })}>Accept</button>
                                                                <button className="icon-btn danger" onClick={() => setDialog({ kind: "reject", referral })}>Reject</button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canDecide ? 7 : 6} className="empty">No referrals match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
